/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePickerWithRange } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useSearch } from "@/hooks/useSearch";
import { getChannelTypeLabel } from "@/lib/constants";
import type { ProcessedChannel } from "@/types";
import { Search, X } from "lucide-react";

export function SearchPopover({ channels }: { channels: ProcessedChannel[] }) {
  const { filters, setFilters, clearFilters, clearFilter } = useSearch();

  const uniqueChannelTypes = [...new Set(channels.map((c) => c.type))];
  const uniqueServers = [
    ...new Set(
      channels.map((c) => c.serverName).filter((c): c is string => Boolean(c)),
    ),
  ];

  const serverOptions = uniqueServers.map((server) => ({
    label: server,
    value: server,
  }));

  const channelTypeOptions = uniqueChannelTypes.map((type) => ({
    label: getChannelTypeLabel(type),
    value: type,
  }));

  const hasActiveFilters =
    Object.values(filters).some((v) =>
      Array.isArray(v) ? v.length > 0 : Boolean(v),
    ) ||
    filters.hasAttachments ||
    filters.showLeftServers;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-[300px] justify-between">
          <div className="flex flex-1 items-center gap-2 overflow-hidden">
            <Search className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {filters.terms || "Search channels and messages..."}
            </span>
          </div>
          {hasActiveFilters && (
            <Badge variant="secondary" className="ml-2 shrink-0">
              Filters Active
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-4" align="start">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Search Terms</Label>
            <div className="relative">
              <Input
                value={filters.terms}
                onChange={(e) => setFilters({ terms: e.target.value })}
                placeholder="Search messages..."
                className="pr-8"
              />
              {filters.terms && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-0 right-0 h-full px-3"
                  onClick={() => clearFilter("terms")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Servers</Label>
            <MultiSelect
              options={serverOptions}
              selectedValues={filters.serverNames}
              onValueChange={(values: string[]) =>
                setFilters({ serverNames: values })
              }
              placeholder="Select servers..."
            />
          </div>

          <div className="space-y-2">
            <Label>Channel Types</Label>
            <MultiSelect
              options={channelTypeOptions}
              selectedValues={filters.channelTypes}
              onValueChange={(values: string[]) =>
                setFilters({ channelTypes: values })
              }
              placeholder="Select channel types..."
            />
          </div>

          <div className="space-y-2">
            <Label>Date Range</Label>
            <DatePickerWithRange
              value={filters.dateRange}
              onChange={(range) => setFilters({ dateRange: range })}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="global-attachments"
                checked={filters.hasAttachments}
                onCheckedChange={(checked) =>
                  setFilters({ hasAttachments: !!checked })
                }
              />
              <label
                htmlFor="global-attachments"
                className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Only show messages with attachments
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="left-servers"
                checked={filters.showLeftServers}
                onCheckedChange={(checked) =>
                  setFilters({ showLeftServers: !!checked })
                }
              />
              <label
                htmlFor="left-servers"
                className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Only show messages from servers you've left
              </label>
            </div>
          </div>

          <Separator />

          <div className="flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
            >
              Clear All Filters
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
